"use client";

import React, { useState } from "react";
import QuestionMark from "@/app/components/icons/questionMark";

interface SubscriptionComparisonProps {
  buttonText: string;
  buttonUrl: string;
  icon?: React.ReactNode;
  buttonClassName?: string;
}

interface Plan {
  name: string;
  price: string;
  buttonText: string;
  buttonUrl: string;
}

const SubscriptionComparison: React.FC<SubscriptionComparisonProps> = ({}) => {
  const [selectedPlan, setSelectedPlan] = useState("Pro"); // Default selected plan
  const [hoveredTooltip, setHoveredTooltip] = useState<number | null>(null);

  const plans: Plan[] = [
    {
      name: "Basic",
      price: "$250/week",
      buttonText: "Subscribe",
      buttonUrl: "/contact",
    },
    {
      name: "Pro",
      price: "$400/week",
      buttonText: "Subscribe",
      buttonUrl: "/contact",
    },
    {
      name: "Ultra",
      price: "$750/week",
      buttonText: "Subscribe",
      buttonUrl: "/contact",
    },
    {
      name: "Max",
      price: "$1000/week",
      buttonText: "Subscribe",
      buttonUrl: "/contact",
    },
  ];

  const services = [
    {
      name: "Website",
      description: "This is a website service for your needs.",
      availability: [true, true, true, true],
    },
    {
      name: "Filling",
      description: "Filling service for necessary forms.",
      availability: [true, true, true, true],
    },
    {
      name: "Maintenance",
      description: "Regular updates and maintenance services.",
      availability: [true, true, true, true],
    },
    {
      name: "SEO Core",
      description: "Optimized SEO core for better visibility.",
      availability: [false, true, true, true],
    },
    {
      name: "SEO Articles",
      description: "Content creation for SEO optimization.",
      availability: [false, true, true, true],
    },
    {
      name: "Facebook Marketing",
      description: "Social media marketing via Facebook.",
      availability: [false, false, true, true],
    },
    {
      name: "Google Marketing",
      description: "Marketing campaigns on Google.",
      availability: [false, false, true, true],
    },
    {
      name: "Free Board System",
      description: "Free board system for managing projects.",
      availability: [false, false, true, true],
    },
    {
      name: "Modern Framework",
      description: "Utilizing modern frameworks for web design.",
      availability: [false, false, false, true],
    },
    {
      name: "Mobile App",
      description: "Developing mobile apps for your business.",
      availability: [false, false, false, true],
    },
    {
      name: "Graphic Designs",
      description: "Custom graphic designs for branding.",
      availability: [false, false, false, true],
    },
    {
      name: "Brandbook",
      description: "Design a professional brand book.",
      availability: [false, false, false, true],
    },
  ];
  const [hoveredServiceTooltip, setHoveredServiceTooltip] = useState<
    number | null
  >(null);
  const [hoveredPlanTooltip, setHoveredPlanTooltip] = useState<number | null>(
    null
  );

  const renderCheckmark = (available: boolean) => {
    return available ? (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="mx-auto"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.82321 13.8232L3.88258 9.88258C3.59762 9.59762 3.59762 9.09762 3.88258 8.82321C4.16754 8.54791 4.66754 8.54791 4.9525 8.82321L7.82321 11.6949L15.0475 4.47064C15.3325 4.19491 15.8325 4.19491 16.1175 4.47064C16.4025 4.74664 16.4025 5.24664 16.1175 5.47064L8.11758 13.4706C7.83258 13.7466 7.33258 13.7466 7.04758 13.4706L7.82321 13.8232Z"
          fill="#3B82F6"
        />
      </svg>
    ) : null;
  };

  return (
    <div className="w-full 2xl:w-[1600px] mx-auto">
      <h3 className="text-center text-[2rem] mb-6">
        Compare Our Subscriptions - Find Your Fit!
      </h3>
      <div className="grid overflow-x-scroll lg:overflow-x-visible grid-cols-5 grid-rows-[auto] md:gap-x-4">
        <div className="flex flex-col group justify-between">
          <div className="row-start-1 col-start-1 flex-col flex items-center rounded-t-[25px] justify-center invisible p-4 h-[120px]">
            Services
          </div>
          {services.map((service, rowIndex) => (
            <div
              key={`service-${rowIndex}`}
              className="row-start-auto border-r col-start-1 flex items-center justify-start px-2 py-2 md:px-4 md:py-6 justify-center h-[50px] md:h-[75px]"
            >
              <div className="flex items-center w-full justify-end gap-2 relative">
                <p className="text-[0.60rem] sm:text-[1rem] lg:pb-[1px] text-right w-[85%]">
                  {service.name}
                </p>
                <div
                  onMouseEnter={() => setHoveredServiceTooltip(rowIndex)}
                  onMouseLeave={() => setHoveredServiceTooltip(null)}
                  className="relative"
                >
                  <QuestionMark />
                  {hoveredServiceTooltip === rowIndex && (
                    <div
                      className="absolute left-full top-1/2 transform -translate-y-1/2 bg-gray-100 p-1 border border-gray-300 rounded-md shadow-lg w-[120px] z-10"
                      style={{ marginLeft: "10px" }}
                    >
                      <p className="text-gray-800 text-[12px]">
                        <p className="text-gray-800 text-[12px]">
                          {service.description}
                        </p>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {plans.map((plan, index) => (
          <div
            key={index}
            className={`flex flex-col rounded-[25px] group transition-colors duration-500 ${
              selectedPlan === plan.name ? "bg-blue-50" : "bg-white"
            }`}
            onMouseEnter={() => setSelectedPlan(plan.name)}
            onMouseLeave={() => setSelectedPlan("Pro")}
          >
            <div
              className={`row-start-1 col-span-1 flex flex-col gap-2 items-center justify-center rounded-t-[25px] p-4 font-bold ${
                selectedPlan === plan.name ? "bg-blue-50" : "bg-white"
              } group-hover:bg-blue-50 duration-500`}
            >
              <div
                className={`flex w-[90%] flex-col px-4 py-2 transition-colors rounded-[25px] duration-500 gap-2 items-center justify-center ${
                  selectedPlan === plan.name ? "lg:bg-[#FAFEFF]" : "bg-white"
                }`}
              >
                <h4 className="leading-none text-[1rem] 2xl:text-[2.25rem] break-words text-blue-500">
                  {plan.name}
                </h4>
                <div
                  className="relative flex items-center flex-col xl:flex-row gap-2"
                  onMouseEnter={() => setHoveredPlanTooltip(index)}
                  onMouseLeave={() => setHoveredPlanTooltip(null)}
                >
                  <p className="text-[0.7rem] md:text-[1rem]">{plan.price}</p>
                  <QuestionMark />
                  {hoveredPlanTooltip === index && (
                    <div
                      className="absolute xl:left-full top-1/2 transform w-[100px] -translate-y-1/2 bg-gray-100 p-1 border border-gray-300 rounded-md shadow-lg z-10"
                      style={{ marginLeft: "10px" }}
                    >
                      <p className="text-gray-800 text-[12px]">
                        Min. contract term: 6 months
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {services.map((service, rowIndex) => (
              <div
                key={`service-${rowIndex}-plan-${index}`}
                className={`row-start-auto rounded-b-[25px] ${
                  rowIndex !== 0 ? "border-t" : ""
                } col-span-1 flex items-center justify-center px-6 py-[1.65rem] group-hover:bg-blue-50 transition-colors duration-500 h-[50px] md:h-[75px]`}
              >
                {renderCheckmark(service.availability[index])}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionComparison;
